'use client';

import { useEffect } from 'react';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

/**
 * Global Error Handler - Catches errors in root layout. Must include html/body tags.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('Global error handler caught an error:', error);
  }, [error]);

  const isDev = process.env.NODE_ENV === 'development';

  const containerStyle = { maxWidth: '42rem', width: '100%', padding: '2rem 1rem' };
  const cardStyle = {
    backgroundColor: '#1f2937',
    borderRadius: '0.75rem',
    border: '1px solid #374151',
    padding: '2rem',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  };
  const iconWrapperStyle = {
    flexShrink: 0,
    width: '3rem',
    height: '3rem',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    borderRadius: '9999px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const detailsBoxStyle = {
    backgroundColor: '#0a0a0a',
    borderRadius: '0.5rem',
    padding: '1rem',
    border: '1px solid #374151',
  };
  const btnBaseStyle = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  };

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Error - VibeAtlas</title>
      </head>
      <body style={{ margin: 0, padding: 0, backgroundColor: '#111827', color: '#e5e7eb', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={containerStyle}>
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={iconWrapperStyle}>
                <AlertTriangle style={{ width: '1.5rem', height: '1.5rem', color: '#ef4444' }} />
              </div>
              <div style={{ flex: 1 }}>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fff', margin: '0 0 0.5rem 0' }}>
                  Critical Error
                </h1>
                <p style={{ color: '#9ca3af', margin: 0 }}>
                  A critical error occurred and the application could not recover.
                </p>
              </div>
            </div>
            {isDev && (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={detailsBoxStyle}>
                  <h2 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#f87171', marginTop: 0, marginBottom: '0.75rem' }}>
                    Development Error Details
                  </h2>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.25rem 0' }}>Message:</p>
                    <p style={{ fontSize: '0.875rem', color: '#d1d5db', fontFamily: 'monospace', margin: 0, wordBreak: 'break-word' }}>
                      {error.message || 'No error message available'}
                    </p>
                  </div>
                  {error.digest && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.25rem 0' }}>Digest:</p>
                      <p style={{ fontSize: '0.875rem', color: '#9ca3af', fontFamily: 'monospace', margin: 0 }}>
                        {error.digest}
                      </p>
                    </div>
                  )}
                  {error.stack && (
                    <details style={{ marginTop: '0.75rem' }}>
                      <summary style={{ fontSize: '0.875rem', color: '#9ca3af', cursor: 'pointer' }}>
                        Stack Trace
                      </summary>
                      <pre style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6b7280', overflowX: 'auto', padding: '0.75rem', backgroundColor: '#000', borderRadius: '0.25rem', border: '1px solid #1f2937' }}>
                        {error.stack}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            )}
            {!isDev && (
              <div style={{ ...detailsBoxStyle, marginBottom: '1.5rem' }}>
                <p style={{ fontSize: '0.875rem', color: '#9ca3af', margin: 0 }}>
                  A critical error prevented the application from loading. Please try again or contact support.
                </p>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button onClick={reset} style={{ ...btnBaseStyle, backgroundColor: '#3b82f6', color: '#fff' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}>
                <RefreshCw style={{ width: '1rem', height: '1rem' }} />
                Try Again
              </button>
              <button onClick={() => window.location.href = '/'} style={{ ...btnBaseStyle, backgroundColor: '#374151', color: '#fff', border: '1px solid #4b5563' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#4b5563'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#374151'}>
                <Home style={{ width: '1rem', height: '1rem' }} />
                Go Home
              </button>
            </div>
          </div>
          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
              Need help? <a href="https://github.com/yourusername/vibeatlas/issues" style={{ color: '#60a5fa', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">Report an issue</a> or contact support.
            </p>
          </div>
        </div>
      </body>
    </html>
  );
}
